import { useState, useEffect, useCallback, type FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/videos';

const Navbar: FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 50);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-100/95 backdrop-blur-md shadow-[0_1px_0_oklch(var(--foreground-50)/0.05)]'
          : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-[1400px] mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer group">
            <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-play-fill text-background-50 text-lg"></i>
            </div>
            <span className="text-xl font-heading font-bold text-foreground-50 tracking-wider group-hover:text-primary-400 transition-colors duration-200">
              棒棒TV
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {categories.filter(c => c.slug !== 'all').map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`whitespace-nowrap px-3 py-2 text-sm rounded-lg transition-all duration-200 cursor-pointer ${
                  location.pathname.includes(cat.slug)
                    ? 'text-primary-400 bg-primary-500/10'
                    : 'text-foreground-200 hover:text-foreground-50 hover:bg-background-200/50'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="hidden md:flex items-center bg-background-200/60 rounded-full px-3 py-1.5 border border-background-300/40">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="搜索视频..."
                className="bg-transparent text-sm text-foreground-100 placeholder-foreground-500 ml-2 w-40 outline-none border-none"
              />
            </div>

            {/* About Link */}
            <Link
              to="/about"
              className={`hidden md:flex whitespace-nowrap text-sm px-4 py-2 rounded-full cursor-pointer transition-all duration-200 ${
                location.pathname === '/about'
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-200 hover:text-foreground-50 border border-background-300/40 hover:border-primary-500/50'
              }`}
            >
              关于我们
            </Link>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer text-foreground-200 hover:text-foreground-50 transition-colors"
              aria-label="菜单"
            >
              <i className={`text-lg ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-[500px] pb-4' : 'max-h-0'
          }`}
        >
          <div className="flex flex-col gap-1">
            {/* Search on Mobile */}
            <div className="flex items-center bg-background-200/60 rounded-lg px-3 py-2 border border-background-300/40 mb-2">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                type="text"
                placeholder="搜索视频..."
                className="bg-transparent text-sm text-foreground-100 placeholder-foreground-500 ml-2 flex-1 outline-none border-none"
              />
            </div>

            {categories.filter(c => c.slug !== 'all').map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`whitespace-nowrap px-4 py-2.5 text-sm rounded-lg transition-all duration-200 cursor-pointer ${
                  location.pathname.includes(cat.slug)
                    ? 'text-primary-400 bg-primary-500/10'
                    : 'text-foreground-200 hover:text-foreground-50 hover:bg-background-200/50'
                }`}
              >
                <i className={`${cat.icon} mr-2`}></i>
                {cat.name}
              </Link>
            ))}

            <Link
              to="/about"
              className="whitespace-nowrap px-4 py-2.5 text-sm rounded-lg transition-all duration-200 cursor-pointer text-foreground-200 hover:text-foreground-50 hover:bg-background-200/50"
            >
              <i className="ri-information-line mr-2"></i>
              关于我们
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;